import { useState, useMemo } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  createColumnHelper,
  type SortingState,
  type RowSelectionState,
} from '@tanstack/react-table'
import { ArrowUpDown, ArrowUp, ArrowDown, Trash2, Tag, ExternalLink, FlaskConical, CheckCircle2, XCircle, Lock, Send } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { FieldHint } from '@/components/field-hint'
import type { Lead, LeadStatus } from './types'
import { KANBAN_COLUMNS } from './types'
import { useUpdateLead, useDeleteLead } from './use-leads'
import { AddToCampaignDialog } from './add-to-campaign-dialog'

const columnHelper = createColumnHelper<Lead>()

function getInitials(lead: Lead): string {
  const first = lead.first_name?.[0] ?? ''
  const last = lead.last_name?.[0] ?? ''
  return (first + last).toUpperCase() || '?'
}

function statusLabel(status: LeadStatus): string {
  const col = KANBAN_COLUMNS.find((c) => c.id === status)
  return col?.label ?? status.replace(/_/g, ' ')
}

function statusBadgeClass(status: LeadStatus): string {
  switch (status) {
    case 'new':
    case 'discovered':
    case 'warmup':
    case 'warmup_done':
      return 'bg-[rgba(255,255,255,0.05)] text-[var(--color-silver-bright)] border-[rgba(255,255,255,0.06)]'
    case 'connection_sent':
      return 'bg-[rgba(28,63,255,0.12)] text-[var(--color-cobalt-bright)] border-[rgba(28,63,255,0.25)]'
    case 'connected':
      return 'bg-cyan-100 text-cyan-700 border-cyan-200'
    case 'contacted':
    case 'dm_sent':
      return 'bg-sky-100 text-sky-700 border-sky-200'
    case 'followup_1_sent':
      return 'bg-violet-100 text-violet-700 border-violet-200'
    case 'followup_2_sent':
      return 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200'
    case 'followup_3_sent':
      return 'bg-pink-100 text-pink-700 border-pink-200'
    case 'replied':
      return 'bg-indigo-100 text-indigo-700 border-indigo-200'
    case 'qualified':
      return 'bg-[rgba(28,63,255,0.12)] text-[var(--color-cobalt-bright)] border-[rgba(28,63,255,0.25)]'
    case 'meeting_booked':
      return 'bg-orange-100 text-orange-700 border-orange-200'
    case 'won':
      return 'bg-emerald-500/12 text-emerald-300 border-emerald-500/25'
    case 'lost':
      return 'bg-red-500/12 text-red-300 border-red-500/25'
  }
}

function relativeTime(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime()
  const minutes = Math.floor(diff / 60_000)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

type SortHeaderProps = {
  readonly label: string
  readonly isSorted: false | 'asc' | 'desc'
  readonly onSort: () => void
  readonly hint?: string
}

function SortHeader({ label, isSorted, onSort, hint }: SortHeaderProps) {
  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={onSort}
        className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
      >
        {label}
        {isSorted === false && <ArrowUpDown className="h-3 w-3" />}
        {isSorted === 'asc' && <ArrowUp className="h-3 w-3" />}
        {isSorted === 'desc' && <ArrowDown className="h-3 w-3" />}
      </button>
      {hint && <FieldHint label={`What is ${label}?`}>{hint}</FieldHint>}
    </div>
  )
}

type BulkActionBarProps = {
  readonly selectedCount: number
  readonly onChangeStatus: (status: LeadStatus) => void
  readonly onAddToCampaign: () => void
  readonly onDelete: () => void
  readonly onClear: () => void
}

function BulkActionBar({ selectedCount, onChangeStatus, onAddToCampaign, onDelete, onClear }: BulkActionBarProps) {
  return (
    <div className="flex items-center gap-3 rounded-lg border bg-primary/5 px-4 py-2">
      <span className="text-sm font-medium">{selectedCount} selected</span>
      <div className="flex items-center gap-2">
        <Button size="sm" className="h-8 text-xs" onClick={onAddToCampaign}>
          <Send className="h-3 w-3" />
          Add to Campaign
        </Button>
        <Select onValueChange={(v) => onChangeStatus(v as LeadStatus)}>
          <SelectTrigger className="h-8 w-40 text-xs">
            <div className="flex items-center gap-1.5">
              <Tag className="h-3 w-3" />
              <SelectValue placeholder="Change status" />
            </div>
          </SelectTrigger>
          <SelectContent>
            {KANBAN_COLUMNS.map((col) => (
              <SelectItem key={col.id} value={col.id} className="text-xs">
                {col.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          size="sm"
          variant="destructive"
          className="h-8 text-xs"
          onClick={onDelete}
        >
          <Trash2 className="h-3 w-3" />
          Delete
        </Button>
        <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={onClear}>
          Clear
        </Button>
      </div>
    </div>
  )
}

type LeadsTableProps = {
  readonly leads: readonly Lead[]
  readonly onLeadClick: (lead: Lead) => void
  /** When provided, the table uses page-level selection state instead of its
   *  own. The shared bulk-action bar in LeadsPage then drives both views. */
  readonly selectMode?: boolean
  readonly selectedIds?: ReadonlySet<string>
  readonly onToggleSelect?: (id: string) => void
}

export function LeadsTable({ leads, onLeadClick, selectMode = false, selectedIds, onToggleSelect }: LeadsTableProps) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [internalRowSelection, setInternalRowSelection] = useState<RowSelectionState>({})
  const [globalFilter, setGlobalFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [addCampaignOpen, setAddCampaignOpen] = useState(false)

  const updateLead = useUpdateLead()
  const deleteLead = useDeleteLead()

  // External (page-level) selection wins when select mode is on. We mirror
  // it back to react-table's RowSelectionState so checked rows stay
  // visually consistent with the shared bulk-action bar.
  const usingExternal = selectMode && Boolean(selectedIds && onToggleSelect)
  const rowSelection: RowSelectionState = usingExternal
    ? Object.fromEntries(
        leads
          .map((l, idx) => [String(idx), selectedIds!.has(l.id)] as const)
          .filter(([, v]) => v),
      )
    : internalRowSelection

  const filteredLeads = useMemo(() => {
    if (statusFilter === 'all') return leads
    return leads.filter((lead) => lead.status === statusFilter)
  }, [leads, statusFilter])

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(v) => {
              if (usingExternal) {
                // Toggle every visible row through the page-level handler.
                const all = !!v
                for (const lead of filteredLeads) {
                  const isCurrentlySelected = selectedIds!.has(lead.id)
                  if (all !== isCurrentlySelected) onToggleSelect!(lead.id)
                }
              } else {
                table.toggleAllPageRowsSelected(!!v)
              }
            }}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) =>
          usingExternal ? (
            <Checkbox
              checked={selectedIds!.has(row.original.id)}
              onCheckedChange={() => onToggleSelect!(row.original.id)}
              aria-label="Select row"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <Checkbox
              checked={row.getIsSelected()}
              onCheckedChange={(v) => row.toggleSelected(!!v)}
              aria-label="Select row"
              onClick={(e) => e.stopPropagation()}
            />
          ),
        enableSorting: false,
      }),
      columnHelper.accessor(
        (row) => row.full_name ?? `${row.first_name} ${row.last_name}`,
        {
          id: 'name',
          header: ({ column }) => (
            <SortHeader
              label="Name"
              isSorted={column.getIsSorted()}
              onSort={() => column.toggleSorting()}
            />
          ),
          cell: ({ row }) => {
            const lead = row.original
            const name = lead.full_name ?? `${lead.first_name} ${lead.last_name}`
            return (
              <div className="flex items-center gap-2">
                <Avatar className="h-7 w-7 shrink-0">
                  {lead.photo_url && <AvatarImage src={lead.photo_url} alt={name} />}
                  <AvatarFallback className="text-xs">{getInitials(lead)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{name}</p>
                  {lead.company && (
                    <p className="truncate text-xs text-muted-foreground">{lead.company}</p>
                  )}
                </div>
              </div>
            )
          },
        },
      ),
      columnHelper.accessor('job_title', {
        header: ({ column }) => (
          <SortHeader
            label="Job Title"
            isSorted={column.getIsSorted()}
            onSort={() => column.toggleSorting()}
          />
        ),
        cell: ({ getValue }) => (
          <span className="text-sm text-muted-foreground">{getValue() ?? '-'}</span>
        ),
      }),
      columnHelper.accessor('enriched_at', {
        id: 'enriched',
        header: () => (
          <div className="flex items-center gap-1">
            <span className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
              <FlaskConical className="h-3 w-3" />
              Research
            </span>
            <FieldHint label="What is Research?">
              Whether AI has researched this lead&apos;s company and context. Research and enrich are the same action.
            </FieldHint>
          </div>
        ),
        cell: ({ getValue }) => {
          const enrichedAt = getValue()
          return enrichedAt ? (
            <span className="inline-flex items-center gap-1 text-xs text-emerald-300" title={`Enriched ${enrichedAt}`}>
              <CheckCircle2 className="h-3.5 w-3.5" />
              Researched
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <XCircle className="h-3.5 w-3.5" />
              Not researched
            </span>
          )
        },
        enableSorting: true,
      }),
      columnHelper.accessor('industry', {
        header: ({ column }) => (
          <SortHeader
            label="Industry"
            isSorted={column.getIsSorted()}
            onSort={() => column.toggleSorting()}
            hint="The company's industry. Locked until you research (enrich) the lead."
          />
        ),
        cell: ({ getValue, row }) => {
          const industry = getValue()
          if (industry) return <span className="text-xs text-muted-foreground">{industry}</span>
          if (!row.original.enriched_at) {
            return (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground/40">
                <Lock className="h-3 w-3" />
                Enrich
              </span>
            )
          }
          return <span className="text-xs text-muted-foreground">-</span>
        },
      }),
      columnHelper.accessor('location', {
        header: ({ column }) => (
          <SortHeader
            label="Location"
            isSorted={column.getIsSorted()}
            onSort={() => column.toggleSorting()}
          />
        ),
        cell: ({ getValue }) => (
          <span className="text-xs text-muted-foreground">{getValue() ?? '-'}</span>
        ),
      }),
      columnHelper.accessor('status', {
        header: ({ column }) => (
          <SortHeader
            label="Status"
            isSorted={column.getIsSorted()}
            onSort={() => column.toggleSorting()}
            hint="The lead's current pipeline stage."
          />
        ),
        cell: ({ getValue }) => {
          const status = getValue()
          return (
            <Badge className={cn('text-xs border', statusBadgeClass(status))}>
              {statusLabel(status)}
            </Badge>
          )
        },
      }),
      columnHelper.accessor('last_activity_at', {
        header: ({ column }) => (
          <SortHeader
            label="Last Activity"
            isSorted={column.getIsSorted()}
            onSort={() => column.toggleSorting()}
            hint="Time since the most recent message or status change."
          />
        ),
        cell: ({ getValue }) => {
          const v = getValue()
          return (
            <span className="text-xs text-muted-foreground">{v ? relativeTime(v) : '-'}</span>
          )
        },
      }),
      columnHelper.accessor('campaign', {
        header: ({ column }) => (
          <SortHeader
            label="Campaign"
            isSorted={column.getIsSorted()}
            onSort={() => column.toggleSorting()}
            hint="Which campaign this lead belongs to, if any."
          />
        ),
        cell: ({ getValue }) => (
          <span className="text-xs text-muted-foreground">{getValue() ?? '-'}</span>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        cell: ({ row }) => (
          <a
            href={row.original.linkedin_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <ExternalLink className="h-3 w-3" />
            LinkedIn
          </a>
        ),
      }),
    ],
    // Re-build columns when external selection wiring changes — the select
    // column captures `usingExternal`, `selectedIds`, `onToggleSelect`, and
    // `filteredLeads` in closure.
    [usingExternal, selectedIds, onToggleSelect, filteredLeads],
  )

  const table = useReactTable({
    data: filteredLeads as Lead[],
    columns,
    state: { sorting, rowSelection, globalFilter },
    onSortingChange: setSorting,
    onRowSelectionChange: usingExternal ? () => undefined : setInternalRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableRowSelection: true,
  })

  const selectedRowIds = Object.keys(rowSelection).filter((k) => rowSelection[k])
  const selectedLeadIds = selectedRowIds
    .map((idx) => filteredLeads[parseInt(idx, 10)]?.id)
    .filter(Boolean) as string[]

  function handleBulkStatusChange(status: LeadStatus) {
    for (const id of selectedLeadIds) {
      updateLead.mutate({ id, updates: { status } })
    }
    setInternalRowSelection({})
  }

  function handleBulkDelete() {
    for (const id of selectedLeadIds) {
      deleteLead.mutate(id)
    }
    setInternalRowSelection({})
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Search by name or company..."
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          className="h-8 w-60 text-sm"
        />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-8 w-36 text-xs">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">All statuses</SelectItem>
            {KANBAN_COLUMNS.map((col) => (
              <SelectItem key={col.id} value={col.id} className="text-xs">
                {col.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Internal bulk action bar — hidden when select mode is page-driven
          (the page renders its own shared bar above the toolbar). */}
      {!usingExternal && selectedLeadIds.length > 0 && (
        <BulkActionBar
          selectedCount={selectedLeadIds.length}
          onChangeStatus={handleBulkStatusChange}
          onAddToCampaign={() => setAddCampaignOpen(true)}
          onDelete={handleBulkDelete}
          onClear={() => setInternalRowSelection({})}
        />
      )}

      <AddToCampaignDialog
        leadIds={selectedLeadIds}
        leads={leads}
        open={addCampaignOpen}
        onOpenChange={setAddCampaignOpen}
        onAdded={() => setInternalRowSelection({})}
      />

      {/* Table */}
      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground text-sm">
                  No leads found.
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() ? 'selected' : undefined}
                  className="cursor-pointer"
                  onClick={() => onLeadClick(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <p className="text-xs text-muted-foreground">
        {table.getFilteredRowModel().rows.length} of {leads.length} leads
      </p>
    </div>
  )
}
